import React, { useState, useEffect } from 'react';
import { useToast } from '../../hooks/useToast';
import api from '../../utils/api';
import { formatCurrency, formatDate } from '../../utils/helpers';
import { FaEdit, FaTrash, FaCheckCircle, FaTimesCircle, FaSearch, FaDownload } from 'react-icons/fa';

const SubscriptionSettings = () => {
  const { showToast } = useToast();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    tier: '',
    isActive: '',
    paymentStatus: '',
    type: '',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [verificationData, setVerificationData] = useState({
    status: 'completed',
    transactionId: '',
  });
  const [isUpdating, setIsUpdating] = useState(false);

  // Fetch subscriptions on component mount
  useEffect(() => {
    fetchSubscriptions();
  }, [currentPage, filters]);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: 10,
        ...filters,
      });

      // Use the admin endpoint specifically for system administrators
      const response = await api.get(`/admin/subscriptions?${queryParams}`);
      setSubscriptions(response.data.data);
      setTotalPages(Math.ceil(response.data.pagination?.next?.page || 1));
    } catch (error) {
      showToast('Failed to fetch subscriptions', 'error');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value,
    });
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Implement search functionality if your API supports it
    // For now, just use local filtering
    fetchSubscriptions();
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleVerifySubscription = async () => {
    try {
      setIsUpdating(true);
      const response = await api.put('/admin/subscriptions/verify', {
        subscriptionId: selectedSubscription._id,
        status: verificationData.status,
        transactionId: verificationData.transactionId,
      });
      
      if (response.data.success) {
        showToast('Subscription payment verified successfully', 'success');
        setIsModalOpen(false);
        fetchSubscriptions();
      }
    } catch (error) {
      showToast('Failed to verify subscription payment', 'error');
      console.error(error);
    } finally {
      setIsUpdating(false);
    }
  };

  const exportSubscriptions = () => {
    // Convert subscriptions to CSV
    const headers = [
      'ID',
      'Type',
      'Tier',
      'Amount',
      'Billing Cycle',
      'Status',
      'Start Date',
      'End Date',
      'Payment Method',
      'Payment Status',
    ];
    
    const csvData = subscriptions.map((sub) => [
      sub._id,
      sub.user ? 'Parent' : 'School',
      sub.tier,
      sub.amount,
      sub.billingCycle,
      sub.isActive ? 'Active' : 'Inactive',
      new Date(sub.startDate).toLocaleDateString(),
      new Date(sub.endDate).toLocaleDateString(),
      sub.payment.method,
      sub.payment.status,
    ]);
    
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(',')),
    ].join('\n');
    
    // Create download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `subscriptions-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Open verification modal
  const openVerificationModal = (subscription) => {
    setSelectedSubscription(subscription);
    setVerificationData({
      status: 'completed',
      transactionId: '',
    });
    setIsModalOpen(true);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Manage Subscriptions</h1>
        <button
          onClick={exportSubscriptions}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
        >
          <FaDownload className="mr-2" /> Export CSV
        </button>
      </div>

      {/* Search and filters */}
      <div className="bg-white shadow-md rounded-lg p-6 mb-6">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-4 mb-4">
          <div className="flex flex-grow items-center">
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            />
            <button
              type="submit"
              className="ml-2 inline-flex items-center p-2 border border-transparent rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <FaSearch />
            </button>
          </div>
          
          <select
            name="tier"
            value={filters.tier}
            onChange={handleFilterChange}
            className="block px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">All Tiers</option>
            <option value="free">Free</option>
            <option value="essential">Essential</option>
            <option value="growth">Growth</option>
            <option value="plus">Plus</option>
            <option value="ignite">Ignite</option>
            <option value="elevate">Elevate</option>
          </select>
          
          <select
            name="isActive"
            value={filters.isActive}
            onChange={handleFilterChange}
            className="block px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          
          <select
            name="paymentStatus"
            value={filters.paymentStatus}
            onChange={handleFilterChange}
            className="block px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">All Payment Status</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="refunded">Refunded</option>
          </select>
          
          <select
            name="type"
            value={filters.type}
            onChange={handleFilterChange}
            className="block px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">All Types</option>
            <option value="user">Parent</option>
            <option value="school">School</option>
          </select>
        </form>
      </div>

      {/* Subscriptions list */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {loading ? (
          <div className="text-center py-10">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        ) : subscriptions.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-gray-500">No subscriptions found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Subscriber
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Tier
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Amount
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Status
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Period
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Payment
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {subscriptions.map((subscription) => (
                  <tr key={subscription._id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {subscription.user ? (
                          <span className="text-blue-600">
                            {subscription.user.firstName} {subscription.user.lastName}
                          </span>
                        ) : subscription.school ? (
                          <span className="text-green-600">{subscription.school.name}</span>
                        ) : (
                          'Unknown'
                        )}
                      </div>
                      <div className="text-sm text-gray-500">
                        {subscription.user ? 'Parent' : 'School'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 capitalize">{subscription.tier}</div>
                      <div className="text-sm text-gray-500">{subscription.billingCycle}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatCurrency(subscription.amount)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          subscription.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {subscription.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(subscription.startDate)}
                      </div>
                      <div className="text-sm text-gray-500">
                        to {formatDate(subscription.endDate)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 capitalize">
                        {subscription.payment.method}
                      </div>
                      <div
                        className={`text-sm ${
                          subscription.payment.status === 'completed'
                            ? 'text-green-600'
                            : subscription.payment.status === 'pending'
                            ? 'text-yellow-600'
                            : 'text-red-600'
                        }`}
                      >
                        {subscription.payment.status}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => openVerificationModal(subscription)}
                        className="text-indigo-600 hover:text-indigo-900 mr-4"
                        disabled={subscription.payment.status === 'completed'}
                      >
                        Verify
                      </button>
                      <a
                        href={`/admin/subscriptions/${subscription._id}`}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        View
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`${
                currentPage === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              } relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md`}
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`${
                currentPage === totalPages
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              } ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md`}
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{subscriptions.length}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`${
                    currentPage === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-500 hover:bg-gray-50'
                  } relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium`}
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`${
                      page === currentPage
                        ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    } relative inline-flex items-center px-4 py-2 border text-sm font-medium`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`${
                    currentPage === totalPages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-500 hover:bg-gray-50'
                  } relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium`}
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Verification Modal */}
      {isModalOpen && selectedSubscription && (
        <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      Verify Subscription Payment
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Tier: <span className="font-semibold capitalize">{selectedSubscription.tier}</span>
                      </p>
                      <p className="text-sm text-gray-500">
                        Amount: <span className="font-semibold">{formatCurrency(selectedSubscription.amount)}</span>
                      </p>
                      
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700">
                          Payment Status
                        </label>
                        <select
                          value={verificationData.status}
                          onChange={(e) => setVerificationData({ ...verificationData, status: e.target.value })}
                          className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        >
                          <option value="completed">Completed</option>
                          <option value="failed">Failed</option>
                          <option value="refunded">Refunded</option>
                        </select>
                      </div>
                      
                      {verificationData.status === 'completed' && (
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-700">
                            Transaction ID
                          </label>
                          <input
                            type="text"
                            value={verificationData.transactionId}
                            onChange={(e) => setVerificationData({ ...verificationData, transactionId: e.target.value })}
                            placeholder="Enter transaction ID"
                            className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleVerifySubscription}
                  disabled={isUpdating || (verificationData.status === 'completed' && !verificationData.transactionId)}
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 ${
                    isUpdating || (verificationData.status === 'completed' && !verificationData.transactionId)
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700'
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm`}
                >
                  {isUpdating ? 'Processing...' : 'Verify Payment'}
                </button>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionSettings;
